/**
 * **학습 기록 불러오기** (사용자 2026-10-17 "학습된 거 사이트에서 직접 테스트")
 *
 * swSim 일괄 학습이 방덱마다 찾은 상위 공덱 목록(`GET /api/sim/memory`). 방덱의 공덱 하나를 누르면 시뮬 탭이 그 방덱·공덱으로 칸을 채우고
 * 시뮬레이션은 [시뮬레이션] 단추로(SimPage `loadLearned` — 2026-10-20 부터 바로 돌리지 않는다). 공덱 능력치·세트·아티팩트/유물 부옵은 학습 때 실제 룬으로 맞춘 값, 방덱은 실전 룬작 표본.
 *
 * kind='defense' 면 **방덱 학습**(사용자 2026-10-30 "방덱학습 버튼 따로만들어서") — 학습 서버가 새로 찾은 방덱과 그 카운터(`GET /api/sim/defsearch`),
 * 어려운 방덱(가장 센 카운터 점수가 낮은) 먼저. 카운터 공덱의 win 은 카운터 탐색 순위 기준(어려운 방덱 기준 점수)이다.
 */
import React, { useEffect, useMemo, useState } from 'react';
import MonsterImage from '../components/MonsterImage';
import { getFilteredMonsters, getMonsterImgPath } from '../utils/monster';

/** 룬 목표 한 몹 (swSim engine/src/runefit.ts Target) */
export interface FitTarget {
    four: number | null; two: number[]; spd: number; cr: number; acc: number; res: number;
    w: { atk: number; hp: number; def: number; cd: number };
    /** 원본에 가깝게 — 룬피팅(원본 룬작)만. 체·공·방·치피를 이 숫자에서 덜 벗어나게 맞춘다 */
    near?: { hp: number; atk: number; def: number; cd: number; spd?: number };
}
export interface MemoryDeck {
    units: number[];
    win: number;
    turns: number;
    games: number;
    sets: number[][];
    stats: Array<Record<string, number>>;
    art?: Array<Record<number, number>>;
    relics?: number[][][];
    /** 선턴 확률 · 빠른 방덱(실효 공속 상위 25% 판) 상대 승률 — 2026-10-17 부터 학습한 기록에만 */
    first?: number | null;
    fastWin?: number | null;
    /** 덱 설명 (예: 계정 룬 맞춤) */
    label?: string;
    /** 칸마다 낀 룬·아티팩트·유물 원본 (SWEX unit_list[] 한 칸 모양) */
    raw?: any[];
    /** 칸마다 룬 목표 — 세트·공속·치확·효적·효저 문턱·가중 (swSim runefit Target). [AI 룬작에 내 룬 맞추기]가 그대로 쓴다 */
    targets?: Array<FitTarget | null>;
    /** 스킬 순서 — AI 마다 같은 판으로 친 공덱 행동 (swSim src/skillorder.ts) */
    skillOrder?: SkillOrder[];
}
/** 한 AI 의 스킬 순서 — u = 공덱 칸 · t = 방덱 칸(−1 없음) · s = 스킬 번호 · slot = 몇 번째 스킬 */
export interface SkillOrder {
    ai: string; games: number; win: number; actions: number;
    sequences: Array<{ steps: Array<{ u: number; s: number; slot: number; t: number }>; share: number; win: number }>;
    perUnit: Array<Array<{ reach: number; picks: Array<{ s: number; slot: number; share: number; target: number }> }>>;
}
/** 방덱 몹 하나의 실전 룬작 (몹이 낀 능력치 — 리더·점령전 보너스 전) */
export interface MemoryDefUnit {
    stats: Record<string, number>; sets: number[]; art?: Record<number, number>; relics?: number[][]; raw?: any;
    /** 아티팩트 부옵을 [속성, 타입] 으로 나눠 적은 것 — 게임 화면에서 옮겨 적은 방덱 (2026-10-20) */
    arts?: [Array<[number, number]>, Array<[number, number]>];
}
export interface MemoryRecord {
    at: string;
    defense: number[];
    owner?: string | null;
    source: 'site' | 'train';
    decks: MemoryDeck[];
    /** 방덱 룬작 고르기 — 실전 표본에서 뽑은 것(중앙 공속·빠른 룬 …). 없으면 실전 중앙값 능력치 */
    defenseBuilds?: Array<{ label: string; units: MemoryDefUnit[] }>;
    /** 실전 룬 표본이 없어 다른 것으로 학습한 방덱 몹 — set = 시뮬 칸에 넣은 룬, median = 실전 중앙값 (사용자 2026-10-29) */
    estimated?: Array<{ monster: number; how: 'set' | 'median' }>;
    /** 방덱 학습 기록만 — 가장 센 카운터 점수(hard, 낮을수록 어려운 방덱) · 방덱 찾기 확정 단계 막은 비율·순위 · 찾은 조건 */
    search?: { hard: number; held?: number; rank?: number; run: string };
}

/** '이름(속성)' → 이름·속성 */
const split = (s: string | undefined) => {
    const m = /^(.*)\(([^)]*)\)$/.exec(s || '');
    return m ? { name: m[1], attribute: m[2] } : { name: s || '?', attribute: '' };
};

/** 몹 번호 → 사이트 몹(매핑의 family·속성) — 엔진 이름(Nana 등)과 사이트 이름이 달라 번호로 찾는다 (학습하기 SimTrain 도 쓴다) */
export function monstersById(monsterMapping: any): Map<number, { name: string; attribute: string }> {
    const out = new Map<number, { name: string; attribute: string }>();
    for (const [fam, els] of Object.entries<any>(monsterMapping?.monsters || {})) {
        for (const [el, forms] of Object.entries<any>(els || {})) {
            if (!forms || typeof forms !== 'object' || Array.isArray(forms)) continue;
            for (const f of Object.values<any>(forms)) {
                // 몹 번호가 여럿(배열)인 몹도 있다 — 예: 암이프리트 [17115, 19215]
                const raw = f?.unit_master_id;
                for (const v of Array.isArray(raw) ? raw : [raw]) {
                    const id = Number(v);
                    if (id > 0 && !out.has(id)) out.set(id, { name: fam, attribute: el });
                }
            }
        }
    }
    return out;
}

/**
 * **같은 방덱 열쇠** — 리더는 자리까지, 2·3번은 순서 없이, **콜라보 짝은 같은 몹**(풍젠이츠 = 풍기린 — 전적검색 isSameMonster 와 같은 잣대).
 * 학습 기록은 같은 방덱이면 가장 최근 것만 보인다 (사용자 2026-10-29 "전에 학습했던 기록은 새로 학습한 기록이 있다면 없애버리자").
 * 서버(swSim /memory)는 몹 번호로만 거른다 — 콜라보 짝을 몰라 여기서 한 번 더.
 */
export function defenseKeyOf(monsterMapping: any, byId: Map<number, { name: string; attribute: string }>, defense: number[]): string {
    const one = (id: number) => {
        const m = byId.get(Number(id));
        if (!m) return String(id);
        const mate = monsterMapping?.monsters?.[m.name]?.collab;
        return `${[m.name, mate].filter(Boolean).sort()[0]}|${m.attribute}`;
    };
    const [lead, ...rest] = defense.map(one);
    return [lead, ...rest.sort()].join(',');
}
/** 같은 방덱(defenseKeyOf)마다 가장 최근 기록 하나만 — 차례는 받은 그대로 */
export function latestPerDefense(monsterMapping: any, byId: Map<number, { name: string; attribute: string }>, records: MemoryRecord[]): MemoryRecord[] {
    const best = new Map<string, MemoryRecord>();
    for (const r of records) {
        const k = defenseKeyOf(monsterMapping, byId, r.defense);
        const cur = best.get(k);
        if (!cur || r.at > cur.at) best.set(k, r);
    }
    const keep = new Set(best.values());
    return records.filter((r) => keep.has(r));
}

/** 몹 번호 → 자연 등급 (몹 표 stars — 각성 전 등급, 미각성 칸 우선) */
function natStarsById(monsterMapping: any): Map<number, number> {
    const out = new Map<number, number>();
    for (const els of Object.values<any>(monsterMapping?.monsters || {})) {
        for (const forms of Object.values<any>(els || {})) {
            if (!forms || typeof forms !== 'object' || Array.isArray(forms)) continue;
            const stars = Number(forms.Unawaken?.stars ?? forms.Awakened?.stars ?? forms.SecondAwakened?.stars) || 0;
            if (!stars) continue;
            for (const f of Object.values<any>(forms)) {
                const raw = f?.unit_master_id;
                for (const v of Array.isArray(raw) ? raw : [raw]) { const id = Number(v); if (id > 0 && !out.has(id)) out.set(id, stars); }
            }
        }
    }
    return out;
}
/** 학습한 때 — '10/28 19:20' (같은 해) · '2025/10/28 19:20' */
const dateOf = (at: string) => {
    const d = new Date(at);
    if (Number.isNaN(d.getTime())) return '';
    const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return `${d.getFullYear() !== new Date().getFullYear() ? `${d.getFullYear()}/` : ''}${d.getMonth() + 1}/${d.getDate()} ${hm}`;
};
type StarFilter = 'all' | '5' | '4';

export default function SimMemory({ monsterMapping, onClose, onPick, kind = 'attack' }: {
    monsterMapping: any;
    /** attack = 학습 기록(방덱마다 공덱 학습) · defense = 방덱 학습(새로 찾은 방덱 — 카드에 가장 센 카운터 점수) */
    kind?: 'attack' | 'defense';
    onClose: () => void;
    onPick: (rec: MemoryRecord, deck: MemoryDeck, defBuild?: number) => void;
}) {
    const [records, setRecords] = useState<MemoryRecord[] | null>(null);
    const [names, setNames] = useState<Record<number, string>>({});
    const [err, setErr] = useState('');
    /** 고른 방덱(기록 순번) — 넓은 화면은 처음부터 맨 위, 좁은 화면은 방덱 목록부터 */
    const [pick, setPick] = useState<number | null>(null);
    const narrow = typeof window !== 'undefined' && window.innerWidth < 720;
    const title = kind === 'defense' ? '방덱 학습' : '학습 기록';
    useEffect(() => {
        fetch(kind === 'defense' ? '/api/sim/defsearch' : '/api/sim/memory', { credentials: 'include' }).then((r) => r.json())
            .then((d) => { if (d?.success) { setRecords(d.records || []); setNames(d.names || {}); } else setErr(d?.error || `${title}을 받지 못했습니다`); })
            .catch(() => setErr(`${title}을 받지 못했습니다`));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const byId = useMemo(() => monstersById(monsterMapping), [monsterMapping]);
    /**
     * 5성 / 4성 — **방덱 등급**: 방덱에 자연 5성이 하나라도 있으면 5성 방덱, 아니면 4성(이하) 방덱 (사용자 2026-10-28 "5성/4성 구분" →
     * "방덱등급으로 나눠야해" — 처음엔 공덱 기준이었다). 4성 방덱은 학습 때 공덱도 자연 4성 이하로만 찾는다(exhaustive.ts --max-nat).
     * 위 [전체 | 5성 | 4성] 으로 거르고, 방덱 카드에 작은 표도 붙인다.
     */
    const natOf = useMemo(() => natStarsById(monsterMapping), [monsterMapping]);
    const is5 = (r: MemoryRecord) => r.defense.some((id) => (natOf.get(Number(id)) || 0) >= 5);
    const [starF, setStarF] = useState<StarFilter>('all');
    const siteOf = (id: number) => byId.get(Number(id)) || split(names[id]);
    /**
     * **방덱 검색** (사용자 2026-10-29 "학습 기록 창에서도 몹 검색이 가능하게 해 — 방덱검색") — 띄어 쓴 말마다 방덱 몹 하나에 맞아야 한다(`물사령 풍슈`).
     * 말 하나는 몹 고르기 창과 같은 이름 검색(getFilteredMonsters — 영문 이름·한글 별명·한/영 안 바꾼 글자)으로 몹(가문·속성)을 찾는다.
     * 콜라보 짝도 같은 몹 — 별명은 콜라보 쪽(RYU)에만 있고 학습 기록 번호는 원본 쪽(Striker 24511)일 수 있다 (사용자 2026-10-29 "물류라 해도 안뜨는데").
     */
    const [q, setQ] = useState('');
    const terms = useMemo(() => q.trim().split(/\s+/).filter(Boolean)
        .map((t) => new Set(getFilteredMonsters(monsterMapping, '', 0, t).flatMap(([n, info, a]) =>
            [n, info?.collab].filter(Boolean).map((f) => `${f}|${a}`)))), [q, monsterMapping]);
    const hasMobs = (r: MemoryRecord) => terms.every((set) => r.defense.some((id) => { const m = siteOf(id); return set.has(`${m.name}|${m.attribute}`); }));
    const label = (id: number) => {
        const m = siteOf(id);
        return getMonsterImgPath(monsterMapping, m).displayName || m.name;
    };
    /**
     * 같은 몹 조합(룬 방향·리더·룬 맞추기만 다른 덱)은 승률이 가장 높은 하나만 (사용자 2026-10-28 — 물가고일·물머메·풍가고일이 1·2·4·9위로 네 번).
     * 몹 순서(리더)가 달라도 같은 조합으로 본다.
     */
    const uniqDecks = (ds: MemoryDeck[]) => {
        const best = new Map<string, MemoryDeck>();
        for (const d of ds) {
            const k = [...d.units].map(Number).sort((x, y) => x - y).join(',');
            const cur = best.get(k);
            if (!cur || d.win > cur.win) best.set(k, d);
        }
        const keep = new Set(best.values());
        return ds.filter((d) => keep.has(d));
    };
    const shown = useMemo(() => latestPerDefense(monsterMapping, byId, records || []).filter((r) => (starF === 'all' || (starF === '5' ? is5(r) : !is5(r))) && hasMobs(r)).map((r) => ({ ...r, decks: uniqDecks(r.decks) })),
        [records, starF, natOf, terms, byId]); // eslint-disable-line react-hooks/exhaustive-deps

    const face = (id: number, size: number) => {
        const { name, attribute } = siteOf(id);
        const img = getMonsterImgPath(monsterMapping, { name, attribute });
        return img.imgPath
            ? <MonsterImage key={id} imgPath={img.imgPath} alt={label(id)} style={{ width: size, height: size, borderRadius: 7, objectFit: 'contain' }} />
            : <span key={id} title={names[id]} style={{ width: size, fontSize: 9, overflow: 'hidden' }}>{name}</span>;
    };

    /**
     * 전투연구 창과 같은 짜임(사용자 2026-10-21): 왼쪽 방덱 카드(방덱 셋 · 공덱 수 · 가장 높은 승률), 오른쪽 고른 방덱의 공덱 줄
     * (순위 · 공덱 셋 · 선턴/빠른방덱 · 승률 · 불러오기). 좁은 화면은 방덱 목록 → 공덱 목록(‹ 방덱 목록).
     */
    const selIdx = pick != null && pick < shown.length ? pick : (narrow ? null : 0);
    const sel = selIdx != null ? shown[selIdx] : null;
    const pct1 = (x: number) => `${(x * 100).toFixed(1)}%`;
    const deckList = (
        <div style={{ overflowY: 'auto', minHeight: 0, maxHeight: 'calc(86vh - 100px)', ...(narrow ? { flex: 1 } : { width: 186, flexShrink: 0, paddingRight: 4 }) }}>
            {shown.map((r, idx) => {
                const on = idx === selIdx;
                const best = r.decks.reduce((m, d) => Math.max(m, d.win), 0);
                return (
                    <div key={r.defense.join(',') + r.at} onClick={() => setPick(idx)} style={{
                        border: on ? '2px solid #667eea' : '1px solid rgba(102, 126, 234, 0.18)', borderRadius: 12,
                        background: on ? 'linear-gradient(145deg, #f5f7ff 0%, #eceffe 100%)' : '#fff',
                        boxShadow: on ? '0 4px 14px rgba(102, 126, 234, 0.25)' : '0 1px 4px rgba(0, 0, 0, 0.05)',
                        padding: on ? '6px 8px' : '7px 9px', marginBottom: 7, cursor: 'pointer', textAlign: 'left'
                    }}>
                        <div style={{ display: 'flex', gap: 3 }}>{r.defense.map((id) => face(id, 34))}</div>
                        {/* 학습한 때 · 승률 한 줄 (사용자 2026-10-28 "승률과 한 줄에 · 공덱 개수는 적지마 · 걸린 시간은 빼") */}
                        <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800 }}>
                            <span title={is5(r) ? '자연 5성이 들어간 방덱' : '자연 4성 이하 방덱'} style={{
                                flexShrink: 0, borderRadius: 999, padding: '0 6px', fontSize: 10, fontWeight: 800,
                                ...(is5(r) ? { background: 'rgba(230, 68, 159, 0.12)', color: '#c2337f' } : { background: 'rgba(242, 166, 35, 0.15)', color: '#b9770e' })
                            }}>{is5(r) ? '5성' : '4성'}</span>
                            {/* 실전 룬 표본 없이 학습한 방덱 — 넣은 룬·중앙값으로 친 몹이 있다 */}
                            {!!r.estimated?.length && (
                                <span title={`실전 룬 표본이 없어 ${r.estimated.map((e) => `${label(e.monster)}: ${e.how === 'set' ? '넣은 룬' : '실전 중앙값'}`).join(' · ')}으로 학습`} style={{
                                    flexShrink: 0, borderRadius: 999, padding: '0 6px', fontSize: 10, fontWeight: 800, background: 'rgba(120, 120, 140, 0.15)', color: '#77788f'
                                }}>추정</span>
                            )}
                            {/* 방덱 학습은 날짜 대신 가장 센 카운터 점수 — 카드가 좁아 셋이 다 들어가지 않는다 */}
                            {!r.search && <span title={`학습 ${new Date(r.at).toLocaleString('ko-KR')}`} style={{ color: '#8a8fb0', fontWeight: 700, whiteSpace: 'nowrap' }}>{dateOf(r.at)}</span>}
                            {r.search
                                ? <span title={`가장 센 카운터 — 어려운 방덱 기준 점수(낮을수록 어려운 방덱)${r.search.rank ? ` · 방덱 찾기 ${r.search.rank}위` : ''} · ${r.search.run}`} style={{ marginLeft: 'auto', color: '#764ba2', whiteSpace: 'nowrap' }}>카운터 {pct1(r.search.hard)}</span>
                                : <span style={{ marginLeft: 'auto', color: best >= 0.5 ? '#1976d2' : '#d32f2f' }}>{pct1(best)}</span>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
    const repList = sel && (
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, alignSelf: 'flex-start', maxHeight: 'calc(86vh - 100px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', border: '1px solid rgba(102, 126, 234, 0.18)', borderRadius: 12, padding: '8px 10px', background: '#f8faff' }}>
            {narrow && <button type="button" onClick={() => setPick(null)} style={{ alignSelf: 'flex-start', border: 'none', background: 'none', color: '#667eea', fontWeight: 800, fontSize: 12, cursor: 'pointer', padding: '2px 0 6px' }}>‹ 방덱 목록</button>}
            <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                {sel.decks.map((d, i) => {
                    return (
                        <React.Fragment key={i}>
                            <div onClick={() => onPick(sel, d)} title="이 방덱·공덱으로 칸 채우기" style={{
                                display: 'flex', alignItems: 'center', gap: narrow ? 5 : 8, padding: '9px 2px', borderBottom: '1px solid rgba(102, 126, 234, 0.1)', cursor: 'pointer'
                            }}>
                                <span style={{ background: i === 0 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e8ebf5', color: i === 0 ? '#fff' : '#667eea', borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 900, flexShrink: 0 }}>{i + 1}</span>
                                <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>{d.units.map((id) => face(id, narrow ? 28 : 34))}</div>
                                {/* 덱 설명('가상 룬 · 어려운 방덱 50% · 실제 스펙 …')은 보이지 않는다 (사용자 2026-10-28 "이제 그만표시해") */}
                                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                    {!narrow && d.first != null && d.fastWin != null && (
                                        <span title="선턴 확률 · 빠른 방덱(공속 상위 25%) 상대 승률" style={{ fontSize: 11, fontWeight: 700, color: '#888', whiteSpace: 'nowrap' }}>
                                            선턴 {Math.round(d.first * 100)}% · 빠른방덱 {Math.round(d.fastWin * 100)}%
                                        </span>
                                    )}
                                    <span style={{ fontSize: 13, fontWeight: 900, color: d.win >= 0.5 ? '#1976d2' : '#d32f2f', whiteSpace: 'nowrap' }}>{pct1(d.win)}</span>
                                    {!narrow && <span style={{ border: '1.5px solid #667eea', borderRadius: 8, color: '#4b5fd6', background: '#fff', fontSize: 11, fontWeight: 800, padding: '4px 8px', flexShrink: 0 }}>불러오기</span>}
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                background: '#fff', borderRadius: 18, padding: '14px 14px 10px', width: 'min(760px, 96vw)', boxSizing: 'border-box',
                maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }}>
                {/* 제목 — 다른 팝업(첫 스킬 지정)과 같은 단색 굵은 글자. 그라데이션 글자는 다크모드 밝기 보정이 덧씌워져 번져 보였다 (사용자 2026-10-28) */}
                <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: narrow ? 16 : 18, fontWeight: 800, color: '#222', flexShrink: 0 }}>{title}</span>
                    <input value={q} onChange={(e) => { setQ(e.target.value); setPick(null); }} placeholder={narrow ? '방덱 몹 검색' : '방덱 몹 검색 (예: 물사령 풍슈)'} style={{
                        flex: 1, minWidth: 0, maxWidth: 260, border: '1.5px solid rgba(102, 126, 234, 0.3)', borderRadius: 999, padding: narrow ? '4px 10px' : '5px 12px',
                        fontSize: narrow ? 12 : 13, outline: 'none', color: '#333', background: '#fff'
                    }} />
                    {/* 5성 / 4성 거르기 */}
                    <div style={{ marginLeft: 'auto', flexShrink: 0, display: 'inline-flex', gap: 2, padding: 2, borderRadius: 999, background: 'rgba(102, 126, 234, 0.08)', border: '1px solid rgba(102, 126, 234, 0.2)' }}>
                        {([['all', '전체'], ['5', '5성'], ['4', '4성']] as Array<[StarFilter, string]>).map(([k, t]) => (
                            <button key={k} type="button" onClick={() => { setStarF(k); setPick(null); }} style={{
                                border: 'none', borderRadius: 999, padding: narrow ? '3px 10px' : '4px 12px', fontSize: narrow ? 11 : 12, fontWeight: 800, cursor: 'pointer',
                                ...(starF === k ? { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' } : { background: 'transparent', color: '#667eea' })
                            }}>{t}</button>
                        ))}
                    </div>
                </div>
                {err && <div style={{ color: '#d32f2f', fontSize: 13, fontWeight: 700 }}>{err}</div>}
                {!records && !err && <div style={{ color: '#999', fontSize: 13 }}>불러오는 중…</div>}
                {records && !shown.length && <div style={{ color: '#999', fontSize: 13 }}>{q.trim() ? `이 몹이 든 방덱의 ${title}이 없습니다` : `아직 ${title}이 없습니다`}</div>}
                {!!shown.length && (
                    <div style={{ display: 'flex', gap: 10, flex: '1 1 auto', minHeight: 0 }}>
                        {(!narrow || !sel) && deckList}
                        {repList}
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <button type="button" onClick={onClose} style={{ border: 'none', borderRadius: 10, padding: '9px 16px', fontWeight: 800, background: '#eef1f5', color: '#555', cursor: 'pointer' }}>닫기</button>
                </div>
            </div>
        </div>
    );
}
